import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const Chat = () => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [user, setUser] = useState(null);
  const [filiale] = useState('main'); // Default filiale
  const scrollRef = useRef(null);

  useEffect(() => {
    loadUser();
    loadMessages();
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const loadUser = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${backendUrl}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${backendUrl}/api/chat/${filiale}`, { withCredentials: true });
      setMessages(res.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${backendUrl}/api/chat/${filiale}`, {
        content: newMessage,
        type: 'text'
      }, { withCredentials: true });
      
      setNewMessage('');
      loadMessages();
      toast.success(t('chat.messageSent'));
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('chat.sendError'));
    }
  };

  return (
    <div className="space-y-4 sm:space-y-8" data-testid="chat-page">
      <div>
        <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-1 sm:mb-2">
          {t('chat.title')}
        </h1>
        <p className="text-sm sm:text-base text-zinc-400">{t('chat.branch')}: {filiale === 'main' ? t('chat.mainBranch') : filiale}</p>
      </div>

      <Card className="bg-zinc-900 border-zinc-800 h-[calc(100vh-250px)] sm:h-[calc(100vh-300px)]">
        <CardHeader className="border-b border-zinc-800 p-3 sm:p-6">
          <CardTitle className="text-lg sm:text-xl font-bold text-white">{t('chat.groupChat')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0 flex flex-col h-[calc(100%-60px)] sm:h-[calc(100%-80px)]">
          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map(message => {
                const isOwn = message.user_id === user?.user_id;
                
                return (
                  <div 
                    key={message.message_id} 
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${message.message_id}`}
                  >
                    <div 
                      className={`max-w-[70%] rounded-lg p-3 ${
                        isOwn 
                          ? 'bg-primary text-white' 
                          : 'bg-zinc-800 text-zinc-100'
                      }`}
                    >
                      {!isOwn && (
                        <div className="text-xs font-bold text-zinc-400 mb-1">
                          {message.user_name}
                        </div>
                      )}
                      
                      <div className="text-sm">{message.content}</div>
                      
                      <div className="text-xs text-zinc-400 mt-1">
                        {new Date(message.created_at).toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="border-t border-zinc-800 p-3 sm:p-4">
            <div className="flex gap-2">
              <Input 
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder={t('chat.inputPlaceholder')}
                className="bg-zinc-950 border-zinc-800"
                data-testid="message-input"
              />
              <Button 
                onClick={handleSendMessage}
                className="bg-primary hover:bg-primary/90"
                data-testid="send-message-btn"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default Chat;
